import { createSlice } from '@reduxjs/toolkit';
import { LS_NOTIFICATION_TEMPLATES_KEY } from 'features/admin/protocol-editor/constants';
import { readJsonFromLocalStorage } from 'features/store/localStorage';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { CellKey } from 'features/admin/protocol-editor/notification-templates/cellKey';

type TemplateContent = { subject: string; body: string };

type TemplatesByProtocol = Record<string, Record<CellKey, TemplateContent>>;

export type AdminNotificationTemplatesState = {
  templatesByProtocol: TemplatesByProtocol;
};

export function readInitialNotificationTemplates(): AdminNotificationTemplatesState {
  return {
    templatesByProtocol: readJsonFromLocalStorage<TemplatesByProtocol>(
      LS_NOTIFICATION_TEMPLATES_KEY,
      {}
    ),
  };
}

const adminNotificationTemplatesSlice = createSlice({
  name: 'adminNotificationTemplates',
  initialState: readInitialNotificationTemplates,
  reducers: {
    adminSetNotificationTemplate: (
      state,
      action: PayloadAction<{
        protocolId: string;
        cellKey: CellKey;
        subject: string;
        body: string;
      }>
    ) => {
      const { protocolId, cellKey, subject, body } = action.payload;
      const existing = state.templatesByProtocol[protocolId]?.[cellKey];
      if (existing?.subject === subject && existing.body === body) {
        return;
      }
      state.templatesByProtocol[protocolId] ??= {};
      state.templatesByProtocol[protocolId][cellKey] = { subject, body };
    },
    adminClearNotificationTemplate: (
      state,
      action: PayloadAction<{ protocolId: string; cellKey: CellKey }>
    ) => {
      const { protocolId, cellKey } = action.payload;
      const protocolTemplates = state.templatesByProtocol[protocolId];
      if (!protocolTemplates || !(cellKey in protocolTemplates)) {
        return;
      }
      const { [cellKey]: _removed, ...rest } = protocolTemplates;
      if (Object.keys(rest).length === 0) {
        const { [protocolId]: _removedProtocol, ...restProtocols } =
          state.templatesByProtocol;
        state.templatesByProtocol = restProtocols;
      } else {
        state.templatesByProtocol[protocolId] = rest;
      }
    },
    adminClearAllNotificationTemplatesForProtocol: (
      state,
      action: PayloadAction<{ protocolId: string }>
    ) => {
      const { protocolId } = action.payload;
      if (!(protocolId in state.templatesByProtocol)) return;
      const { [protocolId]: _removed, ...rest } = state.templatesByProtocol;
      state.templatesByProtocol = rest;
    },
    adminClearAllNotificationTemplates: state => {
      state.templatesByProtocol = {};
    },
  },
});

export const {
  adminSetNotificationTemplate,
  adminClearNotificationTemplate,
  adminClearAllNotificationTemplatesForProtocol,
  adminClearAllNotificationTemplates,
} = adminNotificationTemplatesSlice.actions;

export const adminNotificationTemplatesReducer =
  adminNotificationTemplatesSlice.reducer;
